<template>
  <div class="simple-estimated-delivery-stats">
    <ul class="box">
      <li
        v-if="audienceImpressions.isVisible"
        class="cell">
        <span class="value">{{ isInventoryFilterRequired ? DASH : audienceImpressions.value }}</span>
        <span class="label">{{ $t('messages.averageAudienceConcentration') }}</span>
      </li>
      <li
        v-if="areImpressionStatsAvailable && showImpression"
        class="cell">
        <loader-small v-if="isLoadingAudienceInfo || isLoadingGeofencesEstimatedDeliveryStatistics" />
        <span
          v-else
          class="value"
          >{{ isInventoryFilterRequired ? DASH : totalImpressions }}</span
        >
        <span class="label">{{ $t('messages.impressionCapacity') }}</span>
      </li>
      <li class="cell">
        <loader-small v-if="isLoadingScreenAndSiteCount || isLoadingGeofencesEstimatedDeliveryStatistics" />
        <span
          v-else
          class="value"
          >{{ isInventoryFilterRequired ? DASH : selectedTypeCount.value }}</span
        >
        <span class="label">{{ selectedTypeCount.label }}</span>
      </li>
      <li class="cell">
        <loader-small v-if="isLoadingCPMInfo || isLoadingGeofencesEstimatedDeliveryStatistics" />
        <span
          v-else
          class="value"
          data-test-id="eCPM"
          >{{ isInventoryFilterRequired ? DASH : eCPM }}</span
        >
        <span class="label">{{ $t('names.eCPM', { n: 1 }) }}</span>
      </li>
      <reach-and-frequency-summary
        :entity="entity"
        :max-reach="unformattedTotalImpressions"
        :use-slot="true">
        <template #default="{ frequency, loading, reach }">
          <li
            v-if="reach"
            class="cell">
            <div class="value">
              <loader-small v-if="loading" />
              <template v-else>{{ reach }}</template>
            </div>
            <div class="label">{{ $t('names.estimatedReach', { n: 1 }) }}</div>
          </li>
          <li
            v-if="frequency"
            class="cell">
            <div class="value">
              <loader-small v-if="loading" />
              <template v-else>{{ frequency }}</template>
            </div>
            <div class="label">{{ $t('names.estimatedFrequency', { n: 1 }) }}</div>
          </li>
        </template>
      </reach-and-frequency-summary>
    </ul>
  </div>
</template>

<script setup lang="ts">
  import numbro from 'numbro';

  import { LoaderSmall } from '@/common/components/controls';
  import Plan from '@/common/models/plan/Plan';
  import { DASH } from '@/globals';

  import ReachAndFrequencySummary from './ReachAndFrequencySummary.vue';
  import unformat = numbro.unformat;

  interface ISimpleEstimatedDeliveryStatsProps {
    audienceImpressions: {
      isVisible: boolean;
      value: string;
    };
    areImpressionStatsAvailable: boolean;
    isLoadingAudienceInfo: boolean;
    totalImpressions: string;
    isLoadingScreenAndSiteCount: boolean;
    selectedTypeCount: {
      value: string;
      label: string;
    };
    isLoadingCPMInfo: boolean;
    eCPM: string;
    entity: Plan;
    isLoadingGeofencesEstimatedDeliveryStatistics?: boolean;
    showImpression?: boolean;
    isInventoryFilterRequired: boolean;
  }

  const props = withDefaults(defineProps<ISimpleEstimatedDeliveryStatsProps>(), {
    showImpression: true,
  });

  const unformattedTotalImpressions = unformat(props.totalImpressions);
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;
  .simple-estimated-delivery-stats {
    position: absolute;
    z-index: 1;
    top: auto;
    right: 5rem;
    bottom: 2.9rem;
    background-color: var(--paneHeaderBackgroundColor);
    box-shadow: var(--panesShadow);
    border-radius: 5px;

    .box {
      list-style-type: none;
      display: flex;
      margin: 0;
      padding: 0.5rem 0;

      .cell {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 1.3rem;
        min-width: 8rem;

        &:not(:last-child) {
          border-right: 0.1rem dashed #d8d9d9;
        }

        .value {
          font-size: 2.4rem;
          color: #000000;
          font-weight: 600;
          line-height: 3rem;
        }

        .label {
          font-size: 1rem;
          color: var(--textMutedColor);
          text-transform: uppercase;
          font-weight: 600;
          line-height: 1.3rem;
        }
      }
    }
  }
</style>
